import {
	getEdition,
	PREMIUM_EDITION,
	UNLICENSED,
	toEdition,
} from '@jira/platform__application-editions/src/edition.tsx';
import {
	JIRA_PRODUCT_DISCOVERY,
	JIRA_SOFTWARE,
} from '@jira/platform__application-editions/src/application.tsx';

import { isToday } from 'date-fns';

import { fetchSiteTraitsResource } from '@jira/router-resources__recommendations/src/fetchSiteTraitsResource.tsx';
import { fetchSiteUserTraitsResource } from '@jira/router-resources__recommendations/src/fetchSiteUserTraitsResource.tsx';
import type { PersonalizationApiTraitData } from '@jira/router-resources__recommendations/src/PersonalizationApiTraitData.types.tsx';
import { postOfficeNav4ProjectsSectionResource } from '@jira/router-resources__post-office/src/conditionalPostOfficeJiraTopBannerResource.tsx';
import { usePlacementPreloadedData } from '@jira/post-office__post-office-provider-preload-placement/src/preload-placement.tsx';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import { isFedRamp } from '@atlaskit/atlassian-context/is-fedramp';
import { fg } from '@atlassian/jira-feature-gating';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { getFirstActivationDate } from '@atlassian/jira-common-util-get-tenant-context';
import { useResource } from '@atlassian/react-resource-router';
import { useRoadmapUpsellAdControls } from './useRoadmapUpsellAdControls.tsx';

function traitListIndicatesOptOut(
	traits: PersonalizationApiTraitData[] | null | undefined,
): boolean {
	if (!traits) {
		return false;
	}
	for (const item of traits) {
		if (
			!item ||
			(item.name !== 'optout_all_messages' && item.name !== 'optout_all_messages_user')
		) {
			// oxlint-disable-next-line no-continue
			continue;
		}
		return item.value === 'true' || item.value === true;
	}
	return false;
}

function placementHasMessages(data: unknown): boolean {
	return (
		typeof data === 'object' &&
		data !== null &&
		'messages' in data &&
		Array.isArray(data.messages) &&
		data.messages.length > 0
	);
}

export const useShowRoadmapsUpsell = (): boolean => {
	const appEditions = useAppEditions();
	const isFedRampInstance = fg('aem_roadmap_in_nav')
		? !isFeatureEnabled('jira', 'jira-roadmap-in-nav')
		: isFedRamp();
	const isSiteAdmin = useIsSiteAdmin();
	const firstActivationDateMs = getFirstActivationDate();
	const jiraSoftwareEdition = getEdition(JIRA_SOFTWARE, appEditions);
	const jpdEdition = getEdition(JIRA_PRODUCT_DISCOVERY, appEditions);
	const { isDismissed } = useRoadmapUpsellAdControls();
	const wasSiteActivatedToday = firstActivationDateMs
		? isToday(new Date(firstActivationDateMs))
		: false;

	const { data: userTraitsData } = useResource(fetchSiteUserTraitsResource);
	const { data: siteTraitsData } = useResource(fetchSiteTraitsResource);
	const hasOptedOut =
		traitListIndicatesOptOut(userTraitsData) || traitListIndicatesOptOut(siteTraitsData);

	const [nav4ProjectsSectionData] = usePlacementPreloadedData(
		postOfficeNav4ProjectsSectionResource,
	);
	const hasProjectsSectionRecommendation = placementHasMessages(nav4ProjectsSectionData);

	return (
		!hasProjectsSectionRecommendation &&
		!hasOptedOut &&
		!wasSiteActivatedToday &&
		!isFedRampInstance &&
		!isDismissed &&
		isSiteAdmin &&
		jiraSoftwareEdition === toEdition(PREMIUM_EDITION) &&
		jpdEdition === toEdition(UNLICENSED) &&
		fg('jpd-roadmaps-in-jira')
	);
};
